import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import {
  FaFileAlt,
  FaUniversalAccess,
  FaLifeRing,
} from "react-icons/fa";
import { LockIcon, ArrowLeftIcon, Menu } from "lucide-react";
import Footer from "./components/Footer";

import Login from "./pages/Login";
import RegisterForm from "./pages/Registration/RegisterForm"
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import AllStudents from "./pages/Admin/AllStudents";
import EditUser from "./pages/EditStudent";
import MessagesInbox from "./pages/Admin/MessagesInbox";
import MessageThread from "./pages/Admin/MessageThread";
import NewMessage from "./pages/Admin/NewMessage";
import Dashboard from "./pages/Dashboard";
import { CartProvider } from "./assets/CartContext";
import logo from "./assets/logo.png";


/**
 * Normalize user role by converting to lowercase and removing extra spaces
 * @param {string} role - The user's role
 * @returns {string} Normalized role
 */
const normalizeRole = (role) => role?.toLowerCase().replace(/\s+/g, "");

/**
 * Protected Route Component
 * Restricts access to specific routes based on user role
 * @param {Object} props
 * @param {Array<string>} props.allowedRoles - Array of roles allowed to access the route
 * @returns {React.ReactElement} Outlet if authorized, Navigate to unauthorized page if not
 */
const ProtectedRoute = ({ allowedRoles }) => {
  const userStr = localStorage.getItem("user");
  if (!userStr) return <Navigate to="/" replace />;

  const user = JSON.parse(userStr);
  const userRole = normalizeRole(user?.role);
  const allowed = allowedRoles.map((r) => normalizeRole(r));

  return allowed.includes(userRole) ? <Outlet /> : <Navigate to="/unauthorized" replace />;
};

/**
 * Unauthorized Component
 * Displays 403 error page when user lacks permissions
 * @returns {React.ReactElement} Unauthorized page with header and footer
 */
const Unauthorized = () => {
  const navigate = useNavigate();

  return (
    <>
      <header className="bg-blue-900/90 backdrop-blur-md shadow-lg sticky top-0 z-50 w-full">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} aria-label="Go to login page">
              <img src={logo} alt="GCRD Logo" className="h-6" />
            </button>
            <button
              className="md:hidden p-2 rounded-full hover:bg-blue-800/60"
              aria-label="Toggle Menu"
            >
              <Menu className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
      </header>

      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-gray-100 px-6">
        <div className="bg-white p-10 rounded-2xl shadow-2xl text-center max-w-md w-full">
          <div className="flex justify-center mb-6">
            <div className="bg-red-100 p-4 rounded-full">
              <LockIcon className="text-red-500 w-10 h-10" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">403 - Unauthorized</h2>
          <p className="text-gray-600 mb-6">You don’t have permission to view this page.</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>

      <Footer />
    </>
  );
};

/**
 * Main App Component
 * Root component that manages routing and authentication
 * Provides CartContext and sets up protected/public routes
 * @returns {React.ReactElement} App with Router and all pages
 */
function App() {
  return (
    <CartProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Login />} />
          <Route path="/registration" element={<RegisterForm />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          

          {/* Single dynamic dashboard for all roles */}
            <Route path="/dashboard" element={<Dashboard />} />

          {/* Admin-only extra routes */}
          <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
            <Route path="/admin/messages" element={<MessagesInbox />} />
            <Route path="/admin/messages/new" element={<NewMessage />} />
            <Route path="/admin/messages/:threadId" element={<MessageThread />} />
            <Route path="/allstudents" element={<AllStudents />} />
            <Route path="/edituser/:id" element={<EditUser />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </CartProvider>
  );
}

export default App;
